import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronIcon, PencilIcon, TrashIcon } from '@/components/catalog/icons';
import {
  ProductInclusionPresetsModal,
  type ProductInclusionPresetsTarget,
} from '@/components/products/ProductInclusionPresetsModal';
import { ProjectFormModal } from '@/components/projects/ProjectFormModal';
import { ProjectPricingSection } from '@/components/projects/ProjectPricingSection';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  ValuePills,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { StatTile } from '@/components/ui/StatTile';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime, pluralise } from '@/lib/format';
import { renderInclusion } from '@/lib/inclusions';
import { denialReason, type ModuleSlug } from '@/lib/permissions';
import {
  CAPACITY_CATEGORIES,
  CAPACITY_STATE_BADGE,
  capacityState,
  soldAgainstPlan,
} from '@/lib/project-capacity';
import type {
  CapacityFigures,
  MasterRef,
  Paginated,
  ProductInclusionPresetBand,
  Project,
  ProjectProductInclusionPresets,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
/*
 * "Bangalore, India" is spelled in ONE place, shared with the forms, the list
 * and the consumer screens. `country: null` is a real state on the cities
 * master — not missing data on the project — and renders as the bare city name.
 */
import { cityLabel } from '@/lib/use-project-options';

/*
 * ⚠️ **A project associates CITIES, not countries.** `countries` / `countryIds`
 * left the contract on 2026-08-26: a city carries its own country, so the
 * separate country association was redundant, and two overlapping geographies
 * is one too many.
 *
 * Two things follow, and both are load-bearing rather than stylistic:
 *
 *  - **Every city is rendered with its country.** City names are ambiguous
 *    across countries, and `city.country` is the only thing on the record that
 *    disambiguates them now.
 *  - **A project's `regions` are its own recorded SEED regions of focus.**
 *    Never derive them from a city or a country. `Country.regionId` is upstream
 *    geography on an unrelated id sequence — joining the two returns India
 *    correctly and Egypt as "South East Asia", which is quietly wrong, and
 *    quietly wrong is worse than obviously wrong because the first rows look
 *    right.
 *
 *    ⚠️ **A SET since migration 095, deployed 2026-08-29.** This bullet read
 *    *"A project's `region` is its own recorded SEED region of focus"* and the
 *    detail item below rendered `project.region.name`. Both the singular read
 *    key and the `regionId` write key are gone: `project.region` now yields
 *    `undefined`, which rendered the "No region recorded" branch on EVERY
 *    project rather than erroring. `[]` is the real unclassified state and is
 *    the only thing that branch may now describe.
 */

/*
 * One constant rather than five inline slugs, so the gating for this page is
 * stated once.
 *
 * `projects` IS in `ModuleSlug` and IS in the permission catalogue, and both
 * halves were checked rather than assumed — this comment previously warned
 * that neither was true and that a basic user would lose the Edit button. That
 * warning was written before either landed and was wrong within the hour;
 * measured live, the seeded sets grant `projects.read` to viewer and
 * `read/create/update` to basic and advanced, with delete added for admin.
 *
 * Which is the CRM guard profile `docs/api/projects.md` describes, so the role
 * path and the permission path agree here and neither narrows the other.
 */
const PROJECTS_MODULE: ModuleSlug = 'projects';

/**
 * One row is all this page asks for from each consumer list. The number it
 * wants is `meta.total`; the row itself exists only to prove the filter was
 * applied — see `filterWasApplied`.
 */
const SAMPLE_PER_PAGE = 1;

/*
 * The four record types that can be raised against a project. All four filter
 * on `?projectId=` and all four are `ON DELETE SET NULL`, which is why one
 * registry drives both the panel and the delete confirmation: the number the
 * page shows and the number the dialog names can never come from two places
 * and disagree.
 */
type ConsumerKey = 'deals' | 'quotes' | 'invoices' | 'deliverables';

interface Consumer {
  key: ConsumerKey;
  /** Panel tile heading. */
  label: string;
  singular: string;
  plural: string;
  /**
   * The list this tile links through to — `null` when there is no such list in
   * this app. See the note on `deliverables` below.
   */
  path: string | null;
}

const CONSUMERS: readonly Consumer[] = [
  { key: 'deals', label: 'Deals', singular: 'deal', plural: 'deals', path: '/deals' },
  { key: 'quotes', label: 'Quotes', singular: 'quote', plural: 'quotes', path: '/quotes' },
  { key: 'invoices', label: 'Invoices', singular: 'invoice', plural: 'invoices', path: '/invoices' },
  /*
   * ⚠️ **`path: null` because `/deliverables` is GONE from the route table.**
   * The old hand-entered entity's pages were deleted; the API resource, the
   * table and its `project_id ON DELETE SET NULL` are all still there.
   *
   * ⚠️ **It is deliberately NOT removed from this registry**, and the reason is
   * the second job the registry does: `DeleteConsequence` reads the same
   * `counts` so the panel and the delete dialog can never name different
   * numbers. Dropping the entry would quietly stop a destructive confirmation
   * counting a consumer whose rows really are `SET NULL`ed by the delete —
   * the "new join table, existing guard" failure, in reverse and by omission.
   * The count is worth more than the tile.
   *
   * The tile therefore still reports a true figure (zero, on every project —
   * the table is empty and nothing in this app can add to it) and simply does
   * not link. ⚠️ Note the link was only ever rendered behind `total > 0`, so
   * leaving `'/deliverables'` here would have been an UNREACHABLE dead link
   * rather than a visible one — which is exactly why it needs saying in the
   * type rather than being left to a condition that happens to hold.
   */
  {
    key: 'deliverables',
    label: 'Deliverables',
    singular: 'deliverable',
    plural: 'deliverables',
    path: null,
  },
];

/**
 * A consumer count, and why `unconfirmed` is one of the outcomes.
 *
 * The four consumer list endpoints are **strip-mode**: an unrecognised query
 * parameter is dropped and the list answers `200`, unfiltered. So a count read
 * from `meta.total` alone cannot tell "no deal references this project" from
 * "the filter never happened and this is every deal in the database" — and the
 * second reads as the first, which is the worst way round. Today's production
 * numbers make that concrete: a stripped filter would report 79 deals against
 * a project nothing has been sold on.
 *
 * So a non-zero total is checked against the row that came back, and a count
 * that cannot be confirmed is shown as unavailable rather than as a number.
 */
type CountState =
  | { status: 'loading' }
  | { status: 'ready'; total: number }
  | { status: 'unconfirmed' }
  | { status: 'failed'; message: string };

type Counts = Record<ConsumerKey, CountState>;

const LOADING_COUNTS: Counts = {
  deals: { status: 'loading' },
  quotes: { status: 'loading' },
  invoices: { status: 'loading' },
  deliverables: { status: 'loading' },
};

/*
 * The project record itself is a four-way outcome. `missing` is a 404 on the
 * record and gets a not-found screen — `Project 12 not found` is a real answer,
 * not a failure — while `error` is anything else and gets a retry.
 */
type RecordState =
  | { status: 'loading' }
  | { status: 'ready'; project: Project }
  | { status: 'missing' }
  | { status: 'error'; message: string };

// --- pure helpers ---------------------------------------------------------

/**
 * The one field of a consumer row this page reads. `Deal`, `Quote`, `Invoice`
 * and `Deliverable` all declare it, so the four lists narrow to one shape and
 * the count logic is written once.
 */
type ProjectStamped = { project: MasterRef | null };

/**
 * Did the server actually filter by project?
 *
 * Only asked when `total > 0`, because a zero total needs no proof: an
 * unfiltered empty list and a filtered empty list are the same answer.
 *
 * A row whose `project` is null fails the check, and that is the right way
 * round: `project: null` is also what a failed project lookup serialises as —
 * the backend's lookup catches and returns null rather than throwing — so it
 * cannot prove the filter ran, and "cannot confirm" is the honest report.
 */
function filterWasApplied(rows: readonly ProjectStamped[], projectId: number): boolean {
  const first = rows[0];
  if (first === undefined) return false;
  return first.project != null && first.project.id === projectId;
}

function toCountState(page: Paginated<ProjectStamped>, projectId: number): CountState {
  const total = page.meta.total;
  if (total === 0) return { status: 'ready', total: 0 };
  return filterWasApplied(page.data, projectId)
    ? { status: 'ready', total }
    : { status: 'unconfirmed' };
}

/** "3 deals", "3 deals and 1 quote", "3 deals, 1 quote and 2 invoices". */
function listSentence(parts: readonly string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0] as string;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] as string}`;
}

/**
 * The period, as one line. `intake: null` is a normal, meaningful state — the
 * project spans the whole cycle — so it is spelled out rather than dashed.
 */
function periodLabel(project: Project): string {
  const cycle = project.cycle ? project.cycle.name : 'No cycle on file';
  return project.intake ? `${cycle} · ${project.intake.name}` : `${cycle} · whole cycle`;
}

/*
 * What this project sells, in the page header (rendered inline below).
 *
 * ⚠️ **Reads `products`, the SET — never `product`, the deprecated primary.**
 * Migration 156 made a project sell several, and this page is the authoritative
 * record of what one IS, so naming the primary alone hid every SECONDARY —
 * exactly the case 156 was built for, a travel package under a festival
 * project. The omission was silent: a real product was simply absent from the
 * record page describing it, which reads as a project nobody had finished
 * setting up.
 *
 * ⚠️ *SUPERSEDED 2026-09-25 (owner): `productsSummary`, which named the FIRST
 * and counted the rest as `+N`.* The header identity line now renders EVERY
 * product as a wrapping pill (`ValuePills`), no cap, primary first by the
 * read's `ORDER BY`. An empty set renders `ValuePills`' em dash, as the old
 * `null` branch did.
 */


// --- small presentational pieces ------------------------------------------

function ProjectIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 13 9 5 9-5" />
      <path d="m3 17 9 5 9-5" />
    </svg>
  );
}

function BackToProjects() {
  return (
    <Link
      to="/projects"
      className="inline-flex items-center gap-1 rounded-sm text-xs text-fg-muted transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
    >
      <ChevronIcon direction="left" />
      All projects
    </Link>
  );
}

function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function DetailGroup({
  title,
  columns = 'sm:grid-cols-2 lg:grid-cols-3',
  children,
}: {
  title: string;
  columns?: string;
  children: ReactNode;
}) {
  return (
    <PanelBody className="border-b border-border last:border-b-0">
      <h3 className="mb-3 text-xs font-semibold text-fg-secondary">
        {title}
      </h3>
      <dl className={cx('grid gap-x-4 gap-y-3', columns)}>{children}</dl>
    </PanelBody>
  );
}

/**
 * Capacity has three states and collapsing any two of them loses the meaning.
 *
 * `null` is uncapped — no ceiling was ever set. `0` is sold out: a real,
 * deliberate ceiling of zero. A positive number is a number of places. Never
 * `?? 0`, which turns the first into the second.
 *
 * Still no bar, gauge or "places left". Capacity is recorded and **not
 * enforced** — no endpoint refuses a record that exceeds it. Since 2026-10-27
 * (owner) the page shows SOLD against the plan per category instead
 * (`CategoryCapacityValue`): "12 of 30 · target 20", with "Target met" /
 * "Over capacity" badges; over capacity is a fact, never a refusal. This
 * component now renders the derived TOTAL (`project.capacity` = institution +
 * partner planned), and is the whole rendering for an older API that sends no
 * `capacityPlan` / `sold`.
 */
function CapacityValue({ capacity }: { capacity: number | null }) {
  if (capacity === null) return <span className="block text-fg">Uncapped</span>;
  if (capacity === 0) return <span className="block text-fg">Sold out — nought places</span>;
  return <span className="tabular text-fg">{pluralise(capacity, 'place')}</span>;
}

/** One category's sold-against-plan line and its state badge (owner 2026-10-27). */
function CategoryCapacityValue({ sold, figures }: { sold: number; figures: CapacityFigures }) {
  const badge = CAPACITY_STATE_BADGE[capacityState(sold, figures)];
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="tabular text-fg">{soldAgainstPlan(sold, figures)}</span>
      {badge && <Badge tone={badge.tone}>{badge.label}</Badge>}
    </span>
  );
}

function DetailsSkeleton() {
  return (
    <Panel>
      <PanelHeader title="Project details" />
      {Array.from({ length: 2 }, (_, group) => (
        <PanelBody key={group} className="border-b border-border last:border-b-0">
          <Skeleton className="h-2.5 w-24" />
          <div className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }, (_, item) => (
              <div key={item} className="grid gap-1.5">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-3.5 w-32" />
              </div>
            ))}
          </div>
        </PanelBody>
      ))}
    </Panel>
  );
}

/** One consumer tile: what it is, how many, and a way through to them. */
function ConsumerTile({
  consumer,
  state,
  projectId,
}: {
  consumer: Consumer;
  state: CountState;
  projectId: number;
}) {
  /*
    No number and no link in the `unconfirmed` state, on purpose. The total that
    came back is the unfiltered one, so printing it would name a figure that has
    nothing to do with this project, and the link would land on the same
    unfiltered list and look like it agreed.

    The tile is borderless and square because the grid around it draws the
    hairlines (`gap-px` over the border colour) — the house style is lines, not
    a row of separate cards.
  */
  let value: ReactNode;
  let detail: ReactNode = null;
  if (state.status === 'loading') {
    value = <Skeleton className="h-6 w-16" />;
  } else if (state.status === 'ready') {
    value = state.total;
    detail =
      state.total === 0 || consumer.path === null ? null : (
        <TextLink
          to={`${consumer.path}?projectId=${projectId}`}
          className="inline-flex items-center gap-1"
        >
          View {state.total === 1 ? `the ${consumer.singular}` : `all ${consumer.plural}`}
          <ChevronIcon direction="right" />
        </TextLink>
      );
  } else if (state.status === 'unconfirmed') {
    value = <span className="block text-sm font-semibold text-warning">Not confirmed</span>;
  } else {
    value = <span className="block text-sm font-semibold text-danger">Unavailable</span>;
    detail = <span className="block [overflow-wrap:anywhere]">{state.message}</span>;
  }

  return (
    <StatTile
      className="rounded-none border-0"
      label={consumer.label}
      value={value}
      detail={detail}
    />
  );
}

/**
 * What deleting this project will really do — which is two different things,
 * and the counts on this page only speak to one of them.
 *
 * ⚠️ **This used to open "Nothing else is deleted, and nothing blocks it", and
 * the second half is now FALSE.** Migration 067 put `project_id` on the three
 * LINE-ITEM tables as `ON DELETE RESTRICT`, so a project any quote, invoice or
 * deal **line** still names is refused with a 409 naming those counts.
 *
 * The four numbers this page reads are **HEADER** counts — `?projectId=` on
 * each consumer list is an exact match on that record's own `project_id`, which
 * is the column that stayed `ON DELETE SET NULL`. So:
 *
 *  - **Every figure in this dialog is a non-blocking reference.** Even all four
 *    reading zero does not mean the delete will succeed: a quote whose header
 *    names no project can still carry a LINE that does.
 *  - **Nothing counts line items by project**, so the blocking side genuinely
 *    cannot be shown in advance. The server names it in the refusal, and that
 *    message is surfaced verbatim rather than restated from anything here.
 *
 * The split itself is deliberate: a header that loses its project is degraded
 * but still meaningful, while a line that loses its project is left naming
 * cities that were validated against it and nothing now justifies.
 *
 * A figure this client could not read is still said to be missing rather than
 * shown as zero — the panel and this dialog share `counts`, so they cannot name
 * different numbers.
 */
function DeleteConsequence({ counts, cityCount }: { counts: Counts; cityCount: number }) {
  const affected: string[] = [];
  const unavailable: string[] = [];

  for (const consumer of CONSUMERS) {
    const state = counts[consumer.key];
    if (state.status === 'ready') {
      if (state.total > 0) {
        affected.push(pluralise(state.total, consumer.singular, consumer.plural));
      }
    } else {
      unavailable.push(consumer.plural);
    }
  }

  const allKnown = unavailable.length === 0;

  return (
    <>
      <p className="m-0">
        <strong>Refused if any quote, invoice or deal line names this project.</strong>{' '}
        The counts below are of whole documents, not lines, so even four zeroes are not a promise.
      </p>

      {affected.length > 0 && (
        <p className="m-0 mt-2">
          {listSentence(affected)} name this project. They never block a delete — each survives and
          loses its project
          {cityCount > 0
            ? `, and its ${pluralise(cityCount, 'city', 'cities')}`
            : ''}
          .
        </p>
      )}

      {allKnown && affected.length === 0 && (
        <p className="m-0 mt-2">
          No deal, quote, invoice or deliverable <em>header</em> names this project today.
        </p>
      )}

      {!allKnown && (
        <p className="m-0 mt-2 text-warning">
          The {listSentence(unavailable)} {unavailable.length === 1 ? 'count is' : 'counts are'} not
          available, so this is not the full picture.
        </p>
      )}

      <p className="m-0 mt-2">
        This cannot be undone. Setting it inactive instead is never refused.
      </p>
    </>
  );
}

// --- campaign inclusion presets (migration 298) ---------------------------

/*
 * The project's own quantity-band ladders, one per product it sells.
 *
 * ⚠️ `bands: []` is "Uses product defaults" — a line on this project falls
 * back to the product's own ladder. A project ladder with ANY band wins
 * outright, gaps included, so the status text is a fact about the data and is
 * derived from the array, never from a flag the server does not send.
 */
type PresetsState =
  | { status: 'loading' }
  | { status: 'ready'; entries: ProjectProductInclusionPresets[] }
  | { status: 'error'; message: string };

/** "1", "2–4", "5+". `maxQuantity: null` is OPEN-ENDED, never zero. */
function presetRangeLabel(band: ProductInclusionPresetBand): string {
  if (band.maxQuantity === null) return `${band.minQuantity}+`;
  if (band.maxQuantity === band.minQuantity) return String(band.minQuantity);
  return `${band.minQuantity}–${band.maxQuantity}`;
}

function PresetSummary({ bands }: { bands: readonly ProductInclusionPresetBand[] }) {
  if (bands.length === 0) {
    return <span className="text-fg-muted">Uses product defaults</span>;
  }
  return (
    <ul className="m-0 list-none space-y-0.5 p-0">
      {bands.map((band) => (
        <li key={band.id} className="min-w-0 [overflow-wrap:anywhere]">
          <span className="tabular font-medium text-fg">{presetRangeLabel(band)}</span>
          <span className="text-fg-muted"> · </span>
          <span className="text-fg-secondary">
            {band.inclusions.length === 0
              ? '—'
              : band.inclusions.map((entry) => renderInclusion(entry, entry.quantity)).join(', ')}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PresetsSkeleton() {
  return (
    <PanelBody>
      <div className="grid gap-3" aria-hidden="true">
        {Array.from({ length: 2 }, (_, index) => (
          <div key={index} className="grid gap-1.5">
            <Skeleton className="h-3.5 w-40 max-w-full" />
            <Skeleton className="h-3 w-64 max-w-full" />
          </div>
        ))}
      </div>
    </PanelBody>
  );
}

// --- page -----------------------------------------------------------------

export function ProjectDetailPage() {
  const { id: idParam = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { ability } = useAuth();

  /*
   * The read gate, computed here because the return that uses it has to sit
   * below every hook. See that return, further down, for the reasoning.
   */
  const canReadModule = ability.canRead('projects');

  // A non-numeric or non-positive segment can never match a record — and
  // `/projects/abc` is a 422 rather than a 404 on this API, so it is not even a
  // request worth making.
  const projectId = /^\d+$/.test(idParam) ? Number(idParam) : Number.NaN;
  const validId = Number.isInteger(projectId) && projectId > 0;

  const [recordNonce, setRecordNonce] = useState(0);
  const [countsNonce, setCountsNonce] = useState(0);

  /*
   * Each fetch stores the key of the request it answered, so "is this stale?"
   * is derived during render rather than pushed with a `setLoading(true)` at
   * the top of the effect — that extra synchronous render is what
   * react(set-state-in-effect) warns about, and skipping it also removes any
   * window where a new id shows the previous record as settled.
   */
  const recordKey = `${projectId}:${recordNonce}`;
  const countsKey = `${projectId}:${countsNonce}`;

  const [recordResult, setRecordResult] = useState<{ key: string; value: RecordState } | null>(null);
  const [countsResult, setCountsResult] = useState<{ key: string; value: Counts } | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /**
   * True when the delete was refused with the 409 — a quote, invoice or deal
   * LINE still names this project.
   *
   * Kept beside the message rather than folded into it: the message is the
   * SERVER's, word for word, because it is the only thing that knows the line
   * counts. The flag is what lets the banner add the sentence the server does
   * not say — retire it instead — without editing the counts out of the one
   * sentence that has them.
   */
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  const [presetsNonce, setPresetsNonce] = useState(0);
  const presetsKey = `${projectId}:${presetsNonce}`;
  const [presetsResult, setPresetsResult] = useState<{ key: string; value: PresetsState } | null>(
    null,
  );
  /** The product whose project ladder is open in the editor. One modal per page. */
  const [presetTarget, setPresetTarget] = useState<ProductInclusionPresetsTarget | null>(null);

  const state: RecordState = !validId
    ? { status: 'missing' }
    : recordResult?.key === recordKey
      ? recordResult.value
      : { status: 'loading' };

  useEffect(() => {
    if (!validId) return;
    const controller = new AbortController();
    api
      .getProject(projectId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setRecordResult({ key: recordKey, value: { status: 'ready', project: response.data } });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setRecordResult({ key: recordKey, value: { status: 'missing' } });
          return;
        }
        setRecordResult({
          key: recordKey,
          value: {
            status: 'error',
            message: caught instanceof Error ? caught.message : 'Failed to load this project',
          },
        });
      });
    return () => controller.abort();
  }, [projectId, validId, recordKey]);

  /*
   * The four consumer counts, fetched independently of the record.
   *
   * Deliberately NOT gated on the record having loaded: what references a
   * project is a fact about the id, so the two can resolve in parallel, and a
   * save that re-reads the record does not need to re-count four lists —
   * editing a project cannot change what points at it.
   *
   * `perPage: 1` because only `meta.total` and one specimen row are wanted —
   * four requests, and none of them needs a page of rows to answer a count.
   * ⚠️ *SUPERSEDED: "the rate limit is 120/60s shared across the office."* It is
   * **12,000/60s** (corrected 2026-09-17).
   */
  useEffect(() => {
    if (!validId) return;
    const controller = new AbortController();

    /*
     * `projectId` is a declared filter on all four of these lists — one key,
     * one meaning, no cast. It is an EXACT match on each record's own
     * `project_id` and never reaches through to a project's cities, which is
     * exactly what this panel wants to count.
     */
    const params = { projectId, perPage: SAMPLE_PER_PAGE };
    const requests: ReadonlyArray<readonly [ConsumerKey, Promise<Paginated<ProjectStamped>>]> = [
      ['deals', api.listDeals(params, controller.signal)],
      ['quotes', api.listQuotes(params, controller.signal)],
      ['invoices', api.listInvoices(params, controller.signal)],
      ['deliverables', api.listDeliverables(params, controller.signal)],
    ];

    Promise.all(
      requests.map(([key, pending]) =>
        pending.then(
          (page): readonly [ConsumerKey, CountState] => [key, toCountState(page, projectId)],
          (caught: unknown): readonly [ConsumerKey, CountState] => [
            key,
            {
              status: 'failed',
              message: caught instanceof Error ? caught.message : 'The request did not complete.',
            },
          ],
        ),
      ),
    )
      .then((entries) => {
        if (controller.signal.aborted) return;
        const value = { ...LOADING_COUNTS };
        for (const [key, count] of entries) value[key] = count;
        setCountsResult({ key: countsKey, value });
      })
      .catch(() => {
        // `Promise.all` over promises that each settle themselves cannot reject
        // in practice; the guard keeps an abort from surfacing as unhandled.
      });

    return () => controller.abort();
  }, [projectId, validId, countsKey]);

  /*
   * The project's preset ladders. Like the counts, a fact about the id, so it
   * resolves in parallel with the record; a save re-reads only this.
   */
  useEffect(() => {
    if (!validId) return;
    const controller = new AbortController();
    api
      .projectInclusionPresets(projectId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setPresetsResult({ key: presetsKey, value: { status: 'ready', entries: response.data } });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setPresetsResult({
          key: presetsKey,
          value: {
            status: 'error',
            message: caught instanceof Error ? caught.message : 'The request did not complete.',
          },
        });
      });
    return () => controller.abort();
  }, [projectId, validId, presetsKey]);

  const presets: PresetsState =
    presetsResult?.key === presetsKey ? presetsResult.value : { status: 'loading' };

  const counts: Counts = countsResult?.key === countsKey ? countsResult.value : LOADING_COUNTS;
  const countsSettled = CONSUMERS.every((c) => counts[c.key].status !== 'loading');
  const countsHaveGap = CONSUMERS.some((c) => {
    const status = counts[c.key].status;
    return status === 'failed' || status === 'unconfirmed';
  });

  const retryCounts = useCallback(() => setCountsNonce((n) => n + 1), []);

  const project = state.status === 'ready' ? state.project : null;

  /*
   * A project is a CRM record, not a picklist an administrator curates: create
   * and update take any write role, and delete alone is admin. That is what
   * `ability` already encodes — the role decides and the permission list may
   * only narrow it — so neither control branches on `user.role`.
   */
  const canUpdate = ability.canUpdate(PROJECTS_MODULE);
  const canDelete = ability.canDelete(PROJECTS_MODULE);

  /*
   * Per-person section layout (owner 2026-10-20). Above every early return so
   * the hook order never changes. Ids are stored per user: never rename one.
   * The defaults are today's stack, top to bottom, all full width.
   */
  const layout = usePageLayout('project', [
    { id: 'details', label: 'Project details' },
    // Pricing (owner 2026-10-27): directly below the details. A new id, so a
    // saved layout gains it at this default index.
    { id: 'pricing', label: 'Pricing' },
    { id: 'presets', label: 'Campaign inclusion presets' },
    { id: 'consumers', label: 'Sold against this project' },
  ]);

  async function handleDelete() {
    if (!project) return;
    setDeleting(true);
    setDeleteError(null);
    setDeleteBlocked(false);
    try {
      await api.deleteProject(project.id);
      navigate('/projects', { replace: true });
    } catch (caught) {
      // Already deleted elsewhere: the user's intent is satisfied either way.
      if (caught instanceof ApiError && caught.status === 404) {
        navigate('/projects', { replace: true });
        return;
      }
      /*
       * The 409: a quote, invoice or deal LINE still names this project.
       *
       * Migration 067 made those three `project_id` columns
       * `ON DELETE RESTRICT` while the four document-HEADER columns stayed
       * `ON DELETE SET NULL`. The counts this page shows are header counts, so
       * they can be zero across the board and the delete still be refused —
       * which is precisely why the server's own sentence is surfaced unaltered
       * rather than being restated from anything on screen.
       *
       * Switched on `code` and never on the wording, because ONE code carries
       * TWO sentences: the counted one the service produces after counting, and
       * the shorter "still referenced" one it produces when it catches the raw
       * constraint error for a line inserted between the count and the DELETE.
       * A text match would handle one and fall through on the other.
       *
       * Like a 403 and unlike a 422, a 409 body carries no `details` — banner,
       * never field errors.
       */
      if (caught instanceof ApiError && caught.code === 'CONFLICT') {
        setDeleteBlocked(true);
        setDeleteError(caught.message);
        setDeleting(false);
        return;
      }
      /*
       * A 403 carries no `details` — unlike a 422 — so it is a banner and never
       * a per-field error. `denialReason` returns null when the ability still
       * believed the delete was allowed, which is a role changed under a live
       * token; the access token lasts over five days, so that is real.
       */
      setDeleteError(
        caught instanceof ApiError && caught.status === 403
          ? (denialReason(ability, 'delete', PROJECTS_MODULE) ??
            'Your account no longer has permission to delete projects.')
          : caught instanceof Error
            ? caught.message
            : 'Delete failed',
      );
      setDeleting(false);
    }
  }

  /*
   * The read gate, below every hook so the early return cannot change the hook
   * order — and ABOVE the not-found and error branches, deliberately. A reader
   * who may not see projects must not be told whether this one exists: "Project
   * not found" over a record that is merely hidden from them is a wrong answer
   * dressed as a real one.
   *
   * ⚠️ **A detail page needs the same gate as its list.** `/projects/:id` open
   * while `/projects` refuses is the same hole one level down, and a detail page
   * is exactly what a stale bookmark or a link in an email points at.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. And **hiding a page
   * is not refusing a request** — `GET /projects/:id` answers the same to
   * everybody, so nothing here may be worded as though the record were
   * protected. This is a view control.
   *
   * No back link: `/projects` answers this reader with the same refusal.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Records" title="Projects" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  if (state.status === 'missing') {
    return (
      <>
        <PageHeader
          eyebrow={<BackToProjects />}
          title="Project not found"
        />
        <Panel>
          <EmptyState
            icon={<ProjectIcon />}
            title="This project doesn’t exist"
            description="Deleted, or the address is mistyped. Anything it covered is still on file."
            action={
              <Button variant="primary" onClick={() => navigate('/projects')}>
                Back to projects
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <PageHeader
          eyebrow={<BackToProjects />}
          title="Project"
        />
        <Panel>
          <Notice
            tone="danger"
            role="alert"
            className="m-4 mb-0"
            title="Couldn’t load this project."
          >
            <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{state.message}</p>
          </Notice>
          <EmptyState
            icon={<ProjectIcon />}
            title="Nothing to show"
            description="The request didn’t complete."
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant="primary" onClick={() => setRecordNonce((n) => n + 1)}>
                  Try again
                </Button>
                <Button onClick={() => navigate('/projects')}>Back to projects</Button>
              </div>
            }
          />
        </Panel>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <PageHeader eyebrow={<BackToProjects />} title={<Skeleton className="h-7 w-64" />} />
        <div className="space-y-4">
          <DetailsSkeleton />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={<BackToProjects />}
        title={project.name}
        description={
          <>
            {project.isActive ? (
              <Badge tone="success" dot>
                Active
              </Badge>
            ) : (
              <Badge tone="neutral" dot>
                Inactive
              </Badge>
            )}
            <ValuePills
              items={project.products.map((product) => ({ key: product.id, label: product.name }))}
            />
            <span aria-hidden="true">·</span>
            <span className="min-w-0 truncate">{periodLabel(project)}</span>
          </>
        }
        actions={
          <>
            {canUpdate && (
              <Button
                variant="primary"
                leadingIcon={<PencilIcon />}
                onClick={() => setEditOpen(true)}
              >
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                variant="danger"
                leadingIcon={<TrashIcon />}
                onClick={() => {
                  setDeleteError(null);
                  setConfirmOpen(true);
                }}
              >
                Delete
              </Button>
            )}
            <LayoutToolbar layout={layout} />
          </>
        }
      />

      {/* `min-w-0` on each panel: a grid item defaults to min-width:auto, so the presets table would widen the track instead of scrolling inside its wrapper. */}
      <LayoutGrid
        layout={layout}
        render={{
          details: () => (
        <Panel className="min-w-0" aria-labelledby="project-details-heading">
          <PanelHeader
            id="project-details-heading"
            title="Project details"
          />

          <DetailGroup
            title="What is being sold"
          >
            <DetailItem label="Project name">{project.name}</DetailItem>
            {/*
             * ⚠️ **The SET, not the deprecated `product`.** This item is the
             * authoritative statement of what the project sells, and before
             * migration 156 it showed one name — so a project's secondary
             * products were invisible on the project's own page, with nothing
             * on screen to say a name was missing.
             *
             * Every name is listed rather than summarised: the header above
             * already carries the `+N` short form, and a reader who has opened
             * the record has come for the detail. `products[0]` is the primary
             * and leads, by the read's `ORDER BY`.
             *
             * ⚠️ **`seedsLineItems` is deliberately NOT rendered here.** It
             * decides whether the company "+" flow pulls a product into a new
             * document and NOTHING else — it does not mean the project has
             * stopped selling it — so a tick or a badge beside a name in a list
             * headed "What is being sold" would read as the opposite of what it
             * means. It is ticked per product on the edit form, where the
             * question is asked in words.
             */}
            <DetailItem label="Products">
              {project.products.length > 0 ? (
                <ul className="m-0 list-none space-y-0.5 p-0">
                  {project.products.map((product) => (
                    <li key={product.id} className="min-w-0">
                      {product.name}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-fg-subtle">No product on file</span>
              )}
            </DetailItem>
            {project.capacityPlan && project.sold ? (
              <>
                {CAPACITY_CATEGORIES.map((category) => (
                  <DetailItem key={category.key} label={category.label}>
                    <CategoryCapacityValue
                      sold={project.sold![category.key]}
                      figures={project.capacityPlan![category.key]}
                    />
                  </DetailItem>
                ))}
                <DetailItem label="Total capacity">
                  <CapacityValue capacity={project.capacity} />
                </DetailItem>
              </>
            ) : (
              <DetailItem label="Capacity">
                <CapacityValue capacity={project.capacity} />
              </DetailItem>
            )}
            <DetailItem label="Cycle">
              {project.cycle ? (
                project.cycle.name
              ) : (
                <span className="text-fg-subtle">No cycle on file</span>
              )}
            </DetailItem>
            <DetailItem label="Intake">
              {project.intake ? (
                project.intake.name
              ) : (
                <span className="text-fg">The whole cycle</span>
              )}
            </DetailItem>
            <DetailItem label="Status">
              {project.isActive ? (
                <span className="text-fg">Active</span>
              ) : (
                <span className="block text-fg">Inactive</span>
              )}
            </DetailItem>
          </DetailGroup>

          <DetailGroup
            title="Where it runs"
            columns="grid-cols-1"
          >
            {/*
              The project's OWN regions of focus. Recorded, never computed — see
              the file header: a city's or a country's region is a different id
              sequence entirely, and joining them is wrong in a way that reads
              as right.

              Every member is rendered, never a "+N more": the set holds at most
              the whole master (six rows today) and all of them are the answer,
              the same reasoning the companies surface applies to its own.
            */}
            <DetailItem label="Regions of focus">
              {project.regions.length === 0 ? (
                <span className="block text-fg">Unclassified</span>
              ) : (
                <span className="flex flex-wrap gap-1.5">
                  {project.regions.map((region) => (
                    <Badge key={region.id} tone="neutral" variant="outline">
                      {region.name}
                    </Badge>
                  ))}
                </span>
              )}
            </DetailItem>

            <DetailItem label={pluralise(project.cities.length, 'city', 'cities')}>
              {project.cities.length === 0 ? (
                <span className="text-fg-subtle">
                  No cities recorded, so a line on this project has none to name.
                </span>
              ) : (
                <span className="flex flex-wrap gap-1.5">
                  {project.cities.map((city) => (
                    <Badge key={city.id} tone="neutral" variant="outline">
                      {cityLabel(city)}
                    </Badge>
                  ))}
                </span>
              )}
            </DetailItem>
          </DetailGroup>

          <DetailGroup title="Description" columns="grid-cols-1">
            <DetailItem label="Description">
              {project.description && project.description.trim() !== '' ? (
                <span className="block max-w-prose whitespace-pre-wrap">{project.description}</span>
              ) : (
                <span className="text-fg-subtle">—</span>
              )}
            </DetailItem>
          </DetailGroup>

          <PanelFooter>
            <p className="m-0 text-xs text-fg-muted">
              Created {formatDateTime(project.createdAt)}
              {project.createdBy ? ` by ${project.createdBy.name}` : ''}
            </p>
            <p className="m-0 text-xs text-fg-muted">
              Updated {formatDateTime(project.updatedAt)}
              {project.modifiedBy ? ` by ${project.modifiedBy.name}` : ''}
            </p>
          </PanelFooter>
          {/*
            ⚠️ The "updated by" stamp is written when the project is CREATED as
            well as when it is changed, so it is NOT a record of an edit. The
            sentence that said so was removed on the owner's stricter
            no-helper-text ruling (2026-09-05), so an untouched project now
            shows an "Updated" line with nothing marking it as never edited.
          */}
        </Panel>
          ),
          pricing: () => <ProjectPricingSection project={project} />,
          presets: () => (
        <Panel className="min-w-0" aria-labelledby="project-presets-heading">
          <PanelHeader
            id="project-presets-heading"
            title="Campaign inclusion presets"
            actions={
              presets.status === 'error' ? (
                <Button size="sm" onClick={() => setPresetsNonce((n) => n + 1)}>
                  Try again
                </Button>
              ) : null
            }
          />

          {presets.status === 'loading' && <PresetsSkeleton />}

          {presets.status === 'error' && (
            <PanelBody>
              <Notice tone="danger" role="alert" title="Couldn’t load the presets.">
                <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{presets.message}</p>
              </Notice>
            </PanelBody>
          )}

          {presets.status === 'ready' && presets.entries.length === 0 && (
            <EmptyState
              title="No products on this project"
              description="Add a product on the edit form to give it presets."
            />
          )}

          {presets.status === 'ready' && presets.entries.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableWrap>
                  <Table>
                    <THead>
                      <TR>
                        <TH>Product</TH>
                        <TH>Presets</TH>
                        {canUpdate && (
                          <TH>
                            <span className="sr-only">Actions</span>
                          </TH>
                        )}
                      </TR>
                    </THead>
                    <TBody>
                      {presets.entries.map((entry) => (
                        <TR key={entry.product.id}>
                          <TD verticalAlign="top" className="font-medium">
                            {entry.product.name}
                          </TD>
                          <TD verticalAlign="top">
                            <PresetSummary bands={entry.bands} />
                          </TD>
                          {canUpdate && (
                            <TD verticalAlign="top" className="text-right">
                              <Button
                                size="sm"
                                variant="secondary"
                                aria-label={`Edit presets for ${entry.product.name}`}
                                onClick={() =>
                                  setPresetTarget({
                                    id: entry.product.id,
                                    productName: entry.product.name,
                                  })
                                }
                              >
                                Edit
                              </Button>
                            </TD>
                          )}
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </TableWrap>
              </div>

              <ul className="m-0 list-none divide-y divide-border p-0 md:hidden">
                {presets.entries.map((entry) => (
                  <li key={entry.product.id} className="grid gap-2 px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="m-0 min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                        {entry.product.name}
                      </p>
                      {canUpdate && (
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-label={`Edit presets for ${entry.product.name}`}
                          onClick={() =>
                            setPresetTarget({
                              id: entry.product.id,
                              productName: entry.product.name,
                            })
                          }
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                    <div className="text-sm">
                      <PresetSummary bands={entry.bands} />
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Panel>
          ),
          consumers: () => (
        <Panel className="min-w-0" aria-labelledby="project-consumers-heading">
          <PanelHeader
            id="project-consumers-heading"
            title="Sold against this project"
            actions={
              countsSettled && countsHaveGap ? (
                <Button size="sm" onClick={retryCounts}>
                  Try again
                </Button>
              ) : null
            }
          />

          {/*
            Hairline separators from a `gap-px` grid over the border colour,
            rather than four bordered boxes — the house style is lines, not
            cards. Each tile carries `min-w-0` so a long message wraps inside
            its own column instead of widening the whole track.
          */}
          <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
            {CONSUMERS.map((consumer) => (
              <ConsumerTile
                key={consumer.key}
                consumer={consumer}
                state={counts[consumer.key]}
                projectId={project.id}
              />
            ))}
          </div>

          {/*
            🔴 These four counts are DOCUMENT HEADERS only. A quote or invoice
            LINE can name this project while its document does not, and it is
            those lines that refuse a delete — so the tiles can all read nought
            and the delete still 409. The sentence that said so was removed on
            the owner's stricter no-helper-text ruling (2026-09-05); the delete
            confirmation still names the real line counts.
          */}
        </Panel>
          ),
        }}
      />

      <ProjectFormModal
        open={editOpen}
        project={project}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          // Re-read the record rather than trusting the form's copy: the cities
          // come back sorted by name, resolved live and each carrying its own
          // country, so the saved record is the only thing that knows what the
          // set now reads as.
          setRecordNonce((n) => n + 1);
          // The product set may have changed, and the preset list follows it.
          setPresetsNonce((n) => n + 1);
        }}
      />

      <ProductInclusionPresetsModal
        open={presetTarget !== null}
        product={presetTarget}
        project={{ id: project.id, name: project.name }}
        onClose={() => setPresetTarget(null)}
        onSaved={() => setPresetsNonce((n) => n + 1)}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteError(null);
          setDeleteBlocked(false);
        }}
        onConfirm={() => void handleDelete()}
        title={`Delete ${project.name}?`}
        description={<DeleteConsequence counts={counts} cityCount={project.cities.length} />}
        confirmLabel="Delete project"
        loading={deleting}
        {...(deleteError
          ? {
              error: (
                <>
                  {/*
                    The server's sentence, unaltered. On the 409 it is the only
                    place the line-item counts appear at all.
                  */}
                  <span className="block [overflow-wrap:anywhere]">{deleteError}</span>
                  {deleteBlocked && (
                    <span className="mt-1.5 block">
                      Remove this project from those line items, or set it to Inactive instead.
                    </span>
                  )}
                </>
              ),
            }
          : {})}
      />
    </>
  );
}
